import React, { useContext, useEffect, useState } from 'react'
import { Container, DialogContent, Typography } from '@mui/material'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { toast, Toaster } from 'react-hot-toast'
import { useQuery, useQueryClient } from 'react-query'
import { decodeToken } from 'react-jwt'
import { VerificationStatus } from '@playback-rewards/shared-libraries'

import AuthContext from '../../context/AuthContext'
import {
  deleteSecondEmail,
  getSecondEmails,
  removeUserAccount,
  updateUser,
} from '../../services/userService'
import Modal from '../../components/modal'
import ModalButtons from '../../components/userSettings/ModalButtons'
import LoaderContext from '../../context/LoaderContext'
import PageInner from '../../components/layout/PageInner'
import Layout from '../../components/layout/Details'
import {
  handleError,
  isUserFromMobileDevice,
  isUserFrozen,
  sendGA4Event,
  sendRNMessage,
} from '../../utils/utils'
import {
  ANALYTICS_EVENT,
  ERROR_TEXT,
  LOCAL_STORAGE,
  PRIVACY_POLICY_LINK,
  REACT_NATIVE_STATUS,
  SUCCESS_TEXT,
  TERMS_OF_SERVICE_LINK,
} from '../../constants'
import { generateBaseUrlForSecondaryEmail } from '../../services/authService'
import useLocalStorage from '../../hooks/useLocalStorage'
import SelectModal from './components/SelectModal'
import UserSettingsItems from './components/Items'

import './index.css'

const DELETION_MODAL_KEY = {
  STANDARD: 0,
  VERIFIED: 1,
}

const UserSettings = () => {
  const { user, updateUserData, logout } = useContext(AuthContext)
  const { isLoading, showLoader, hideLoader } = useContext(LoaderContext)
  const [searchParams] = useSearchParams()
  const queryClient = useQueryClient()
  const { getItem } = useLocalStorage()

  const [modal, setModal] = useState(false)
  const [selectModal, setSelectModal] = useState(false)
  const [emailItems, setEmailItems] = useState([])
  const [modalProps, setModalProps] = useState({})
  const [needsRenewal, setNeedsRenewal] = useState(false)
  const [hasSeenToast, setSeenToast] = useState(false)
  const [showRealMoneyOffers, setShowRealMoneyOffers] = useState(user?.isRealMoneyOffersEnabled)
  const webVersion =
    process.env.REACT_APP_NODE_ENV === 'staging'
      ? process.env.REACT_APP_BUILD_VERSION?.slice(0, 7)
      : null

  const handleDeleteSecondEmail = (email) => {
    showLoader()

    deleteSecondEmail(email)
      .then((res) => {
        if (res.updatedUser) {
          updateUserData(res.updatedUser)
        }
      })
      .finally(() => {
        queryClient.invalidateQueries({ queryKey: 'secondEmails' })
      })
  }

  const getItemsFromSecondEmails = (emails) => {
    const result = []
    let needsRenewal = false
    emails.map((email) => {
      if (email.grantNotification) {
        needsRenewal = true
      }
      result.push({
        value: email.connectedEmail,
        onClick: email.grantNotification && handleConnectSecondEmail,
        warning: email.grantNotification && '(needs renewal)',
        ...{
          onDelete: () => handleDeleteSecondEmail(email.connectedEmail),
        },
      })
    })

    setNeedsRenewal(needsRenewal)

    setEmailItems(result)
  }

  const { isLoading: loadingSecondEmails } = useQuery(
    'secondEmails',
    () => getSecondEmails().then(getItemsFromSecondEmails).finally(hideLoader),
    { onError: handleError }
  )

  useEffect(() => {
    loadingSecondEmails ? showLoader() : hideLoader()

    if (!loadingSecondEmails && !hasSeenToast) {
      if (searchParams.has('connectError')) {
        if (needsRenewal) {
          toast.error(ERROR_TEXT.reconnect_email)
          return
        }

        toast.error(ERROR_TEXT[searchParams.get('connectError')])
      }

      if (searchParams.has('connectSuccess')) {
        toast.success(SUCCESS_TEXT.ADD_SECOND_EMAIL)
      }

      setSeenToast(true)
    }
  }, [loadingSecondEmails, emailItems.length])

  const navigate = useNavigate()

  const hideModal = () => setModal(false)

  const showSelectModal = () => {
    setSelectModal(true)
  }
  const hideSelectModal = () => setSelectModal(false)

  const handleGoBack = () => {
    let route = '/games'

    if (
      location.key !== 'default' &&
      !searchParams.has('connectError') &&
      !searchParams.has('connectSuccess')
    ) {
      route = -1
    }

    navigate(route)
  }

  const showModal = (key = DELETION_MODAL_KEY.STANDARD) => {
    const standardProps = {
      title: 'Are you sure?',
      text: (
        <>
          Playback only allows one account per person. If you choose to delete this account, you
          will not be able to create another account or recover this account in the future.
        </>
      ),
      actionButtons: (
        <ModalButtons
          hideModal={hideModal}
          button={{
            handleClick: () => handleRemoveAccount(DELETION_MODAL_KEY.STANDARD),
            text: 'Remove Account',
          }}
        />
      ),
    }

    switch (key) {
      case DELETION_MODAL_KEY.STANDARD: {
        setModalProps(standardProps)
        break
      }
      case DELETION_MODAL_KEY.VERIFIED: {
        setModalProps({
          title: 'You won’t be able to create another account!',
          text: (
            <DialogContent>
              <Typography variant="body2">
                Are you sure you want to delete your account? Playback only allows one account per
                person. Since you verified your current account, you will not be able to verify any
                additional accounts.
              </Typography>
              <Typography variant="body2" sx={styles.modalBoldText}>
                <br />
                Once you delete your account, you will not be able to recover it, verify a new
                account, or use Playback ever again.
              </Typography>
            </DialogContent>
          ),
          isCustomText: true,
          actionButtons: (
            <ModalButtons
              hideModal={hideModal}
              closeButtonText="Don’t delete my account"
              button={{
                handleClick: () => handleRemoveAccount(DELETION_MODAL_KEY.VERIFIED),
                text: 'Delete my account',
              }}
            />
          ),
        })
        break
      }
      default:
        {
          setModalProps(standardProps)
        }

        setModal(true)
    }
  }

  const handleRemoveAccount = async (modalKey = DELETION_MODAL_KEY.STANDARD) => {
    if (isUserFrozen(user)) {
      hideModal()
      toast.error(ERROR_TEXT.account_frozen_account_delete, { duration: 10000 })
      return
    }

    if (
      modalKey === DELETION_MODAL_KEY.STANDARD &&
      user?.identityVerification.status === VerificationStatus.VERIFIED
    ) {
      return showModal(DELETION_MODAL_KEY.VERIFIED)
    }

    hideModal()
    showLoader()
    await removeUserAccount()
      .catch(handleError)
      .finally(() => {
        logout()
        hideLoader()
      })
  }

  const handleAdminClick = () => navigate('/admin')

  const handleLogout = async () => {
    await sendGA4Event(ANALYTICS_EVENT.LOGOUT)
    showLoader()
    logout().finally(() => {
      hideLoader()
    })
  }

  const handleConnectSecondEmail = async () => {
    // const authLink = await generateAuthLink('auth/gmail/second-email', ['email']).catch(handleError)
    if (window.ReactNativeWebView) {
      setModal(false)
      const token = await getItem(LOCAL_STORAGE.JWT_TOKEN)
      const decodedToken = decodeToken(token)
      sendRNMessage({
        status: REACT_NATIVE_STATUS.CONNECT_SECOND,
        userId: decodedToken.id,
        shouldNavigateToSettings: true,
      })

      return
    }

    generateBaseUrlForSecondaryEmail().then((authLink) => (window.location.href = authLink))
  }

  const handleRealMoneyOffersToggle = (event) => {
    const isRealMoneyOffersEnabled = event.target.checked

    setShowRealMoneyOffers(isRealMoneyOffersEnabled)
    updateUser({ isRealMoneyOffersEnabled }).then(updateUserData)
  }

  const USER_SETTING_SECTIONS = [
    {
      title: 'General Info',
      items: [
        {
          label: 'Email',
          value: user?.email,
          onClick: null,
        },
        {
          label: 'Preferred Mobile App Store:',
          value: user?.platform,
          ...(!isUserFromMobileDevice() && { onClick: () => showSelectModal(false) }),
        },
        {
          label: 'Show Real Money Gaming Offers',
          switchValue: showRealMoneyOffers,
          onClick: handleRealMoneyOffersToggle,
        },
      ],
    },
    {
      title: 'Receipt Reward Emails',
      items: [
        ...emailItems,
        {
          label: 'Add Email',
          onClick: handleConnectSecondEmail,
          customSX: styles.addEmail,
        },
      ],
    },
    {
      title: 'Support',
    },
    {
      title: 'Legal',
      items: [
        {
          label: 'Privacy Policy',
          onClick: () => {
            window.location.href = PRIVACY_POLICY_LINK
          },
        },
        {
          label: 'Terms of Service',
          onClick: () => {
            window.location.href = TERMS_OF_SERVICE_LINK
          },
          customSX: styles.termsOfService,
        },
      ],
    },
    {
      items: [
        {
          label: 'Admin Panel',
          onClick: handleAdminClick,
          hideItem: user?.role !== 'Admin',
        },
        {
          label: 'Log Out',
          onClick: handleLogout,
        },
        {
          label: 'Delete Account',
          onClick: showModal,
          customSX: styles.deleteAccount,
        },
      ],
    },
  ]

  return (
    <>
      <Toaster />
      <Modal showModal={modal} hideModal={hideModal} {...modalProps} />
      <SelectModal
        showLoader={showLoader}
        hideLoader={hideLoader}
        showModal={selectModal}
        hideModal={hideSelectModal}
        currentItem={user?.platform}
      />
      <PageInner customClassName={isLoading ? 'blured' : ''}>
        <Layout
          title={user ? `${user?.firstName} ${user?.lastName}` : ''}
          handleGoBack={handleGoBack}
        >
          <Container maxWidth="sm" sx={styles.container}>
            <div className="userSettingsInner">
              <Typography variant="inherit">Profile</Typography>
              {USER_SETTING_SECTIONS.map((item, key) => (
                <UserSettingsItems key={item.title + key} {...item} />
              ))}
              {webVersion && (
                <Typography variant="caption" sx={styles.webVersion}>
                  Web version: {webVersion}
                </Typography>
              )}
            </div>
          </Container>
        </Layout>
      </PageInner>
    </>
  )
}

const styles = {
  title: {
    padding: '32px 16px',
    textAlign: 'center',
  },
  container: {
    padding: 0,
  },
  contactUs: {
    display: 'flex',
    justifyContent: 'unset',
    gap: '12px',
    paddingBottom: '12px',
    '@media (max-width: 380px)': {
      gap: '6px',
    },
  },
  termsOfService: {
    display: 'flex',
    justifyContent: 'space-between',
    paddingBottom: '12px',
  },
  addEmail: {
    display: 'flex',
    justifyContent: 'space-between',
    paddingBottom: '12px',
    '& .MuiTypography-root': {
      background: 'linear-gradient(to right, #ec4899, #f59e0b)',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
    },
  },
  deleteAccount: {
    display: 'flex',
    justifyContent: 'space-between',
    paddingBottom: '12px',
    '& .MuiTypography-root': {
      color: '#EF4444',
    },
  },
  webVersion: {
    display: 'block',
    marginTop: '12px',
    textAlign: 'center',
    color: '#71717A',
  },
  modalBoldText: {
    fontWeight: 700,
  },
}

export default UserSettings
